/** CA HT et TVA : montant, référence, écart. */
import { useFormatters } from "../../formats/useFormatters";
import { deltaPct, formatDeltaPct } from "./period";
import type { DashRealized } from "./types";

type RealizedPanelProps = {
  realized?: DashRealized;
};

type Side = {
  kicker: string;
  cents: number | null;
  vat: number | null;
  sub: string;
};

function periodSub(label: string, range: string): string {
  const head = label.split(" ")[0]?.toLowerCase() ?? "";
  if (head && range.toLowerCase().includes(head)) return range;
  return `${label} · ${range}`;
}

function tone(delta: number | null): string {
  if (delta === null || delta === 0) return "";
  return delta > 0 ? " is-up" : " is-down";
}

export function RealizedPanel({ realized }: RealizedPanelProps) {
  const { money } = useFormatters();
  if (!realized) return null;

  function ht(cents: number | null): string {
    return cents === null ? "—" : `${money(cents)} HT`;
  }

  function signed(cents: number | null): string {
    if (cents === null) return "—";
    const sign = cents > 0 ? "+" : "";
    return `${sign}${money(cents)}`;
  }

  function vatLine(cents: number | null, signedAmount: boolean): string {
    if (cents === null) return "TVA —";
    return signedAmount ? `TVA ${signed(cents)}` : `TVA ${money(cents)}`;
  }

  const monthPrevComparable = !realized.month_imported && !realized.month_prev_imported;
  const monthLyComparable = !realized.month_imported && !realized.month_ly_imported;
  const yearComparable = !realized.year_imported && !realized.year_prev_imported;
  const monthName = realized.month_label.split(" ")[0] || "Le mois";

  return (
    <section className="fx-bill fx-real" aria-label="Chiffre d’affaires réalisé">
      <div className="fx-panel__head">
        <h2>Réalisé</h2>
        <span className="fx-mail__hint">Factures envoyées − avoirs · date de pièce</span>
      </div>
      <div className="fx-real__rows">
        <div className="fx-real__pair">
          <h3>Mois en cours</h3>
          <Compare
            current={{
              kicker: "Ce mois",
              cents: realized.month_ca_ht_cents,
              vat: realized.month_vat_cents,
              sub: periodSub(realized.month_label, realized.month_range),
            }}
            previous={{
              kicker: "Mois dernier",
              cents: realized.month_prev_ca_ht_cents,
              vat: realized.month_prev_vat_cents,
              sub: periodSub(realized.month_prev_label, realized.month_prev_range),
            }}
            comparable={monthPrevComparable}
            uneven={!realized.month_imported && !!realized.month_prev_imported ? `${monthName} n’est pas fini` : ""}
            ht={ht}
            signed={signed}
            vatLine={vatLine}
          />
          <Compare
            current={{
              kicker: "Ce mois",
              cents: realized.month_ca_ht_cents,
              vat: realized.month_vat_cents,
              sub: periodSub(realized.month_label, realized.month_range),
            }}
            previous={{
              kicker: "Il y a un an",
              cents: realized.month_ly_ca_ht_cents,
              vat: realized.month_ly_vat_cents,
              sub: periodSub(realized.month_ly_label, realized.month_ly_range),
            }}
            comparable={monthLyComparable}
            uneven={!realized.month_imported && !!realized.month_ly_imported ? `${monthName} n’est pas fini` : ""}
            ht={ht}
            signed={signed}
            vatLine={vatLine}
          />
        </div>
        <div className="fx-real__pair">
          <h3>Année civile</h3>
          <Compare
            current={{
              kicker: "Cette année",
              cents: realized.year_ca_ht_cents,
              vat: realized.year_vat_cents,
              sub: periodSub(realized.year_label, realized.year_range),
            }}
            previous={{
              kicker: "Année d’avant",
              cents: realized.year_prev_ca_ht_cents,
              vat: realized.year_prev_vat_cents,
              sub: periodSub(realized.year_prev_label, realized.year_prev_range),
            }}
            comparable={yearComparable}
            uneven={!realized.month_imported && (realized.year_imported || realized.year_prev_imported) ? "L’année n’est pas finie" : ""}
            ht={ht}
            signed={signed}
            vatLine={vatLine}
          />
        </div>
      </div>
    </section>
  );
}

function Compare({
  current,
  previous,
  comparable,
  uneven,
  ht,
  signed,
  vatLine,
}: {
  current: Side;
  previous: Side;
  comparable: boolean;
  uneven: string;
  ht: (cents: number | null) => string;
  signed: (cents: number | null) => string;
  vatLine: (cents: number | null, signedAmount: boolean) => string;
}) {
  const diff = current.cents === null || previous.cents === null ? null : current.cents - previous.cents;
  const vatDiff = current.vat === null || previous.vat === null ? null : current.vat - previous.vat;
  const pct = comparable && diff !== null && previous.cents !== null ? deltaPct(current.cents ?? 0, previous.cents) : null;
  const hint = comparable ? formatDeltaPct(pct) : uneven;

  return (
    <div className="fx-dash-3">
      <article className="fx-tile">
        <p className="fx-encaiss__k">{current.kicker}</p>
        <p className="fx-encaiss__v">{ht(current.cents)}</p>
        <p className="fx-encaiss__sub">{current.sub}</p>
        <p className="fx-real__vat">{vatLine(current.vat, false)}</p>
      </article>
      <article className="fx-tile">
        <p className="fx-encaiss__k">{previous.kicker}</p>
        <p className="fx-encaiss__v">{ht(previous.cents)}</p>
        <p className="fx-encaiss__sub">{previous.sub}</p>
        <p className="fx-real__vat">{vatLine(previous.vat, false)}</p>
      </article>
      <article className={`fx-tile fx-real__delta${tone(diff)}`}>
        <p className="fx-encaiss__k">Écart</p>
        <p className="fx-encaiss__v">{signed(diff)}{diff === null ? "" : " HT"}</p>
        <p className={comparable ? "fx-encaiss__pct" : "fx-encaiss__sub"}>{hint || "\u00a0"}</p>
        <p className={`fx-real__vat${tone(vatDiff)}`}>{vatLine(vatDiff, true)}</p>
      </article>
    </div>
  );
}
